/**
 * SettingsSecurity — Iter 191t · Permissions › Data & Security
 *
 * Consent provider + custom head/footer scripts, moved out of General.
 *
 * Persistence:
 *   GET  /api/builder/settings          → returns `security_scripts`
 *   PUT  /api/builder/settings { security_scripts: {...} }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Code, ShieldCheck, Save, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULTS = {
  consent_provider: '',
  head_scripts: '',
  footer_scripts: '',
};

const PROVIDER_GUIDES = {
  termly: {
    href: 'https://app.termly.io',
    steps: [
      'Create a free account at app.termly.io.',
      'Add your website URL and run the cookie scan.',
      'Copy the embed snippet from Dashboard → Install.',
      'Paste it into "Head scripts" below.',
    ],
  },
  iubenda: {
    href: 'https://www.iubenda.com',
    steps: [
      'Create a free iubenda account.',
      'Generate a Cookie Solution banner for your site.',
      'Copy the JavaScript embed code.',
      'Paste it into "Head scripts" below.',
    ],
  },
  cookiebot: {
    href: 'https://www.cookiebot.com',
    steps: [
      'Sign up at cookiebot.com.',
      'Add your domain and configure consent categories.',
      'Copy the embed script from the Implementation tab.',
      'Paste it into "Head scripts" below.',
    ],
  },
};

export default function SettingsSecurity() {
  const [state, setState] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/settings`, { headers: auth() });
        setState({ ...DEFAULTS, ...(data?.security_scripts || {}) });
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { security_scripts: state }, { headers: auth() });
      toast.success('Scripts saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const guide = PROVIDER_GUIDES[state.consent_provider];

  return (
    <div className="relative" data-testid="settings-security-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Permissions &amp; Ownership</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Data &amp; Security</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Wire up a consent banner and inject any additional third-party scripts. These are what
              your storefront renders in <span className="font-mono text-white/70">&lt;head&gt;</span> and just before
              <span className="font-mono text-white/70"> &lt;/body&gt;</span>.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="security-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <GlassCard className="p-6" data-testid="security-consent-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={ShieldCheck} ramp="violet" />
                <div>
                  <h2 className="text-white font-bold font-syne">Consent provider</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    We&rsquo;ll show a compact preset guide once you pick a provider so you know exactly what
                    snippet to paste.
                  </p>
                </div>
              </div>

              <Label className="text-[#94A3B8] text-sm">Provider</Label>
              <select
                value={state.consent_provider || ''}
                onChange={(e) => setState((p) => ({ ...p, consent_provider: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm mt-1.5"
                data-testid="security-provider-select"
              >
                <option value="">None</option>
                <option value="termly">Termly</option>
                <option value="iubenda">Iubenda</option>
                <option value="cookiebot">CookieBot</option>
                <option value="custom">Custom / other</option>
              </select>

              {guide && (
                <div className="mt-4 p-3.5 rounded-xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/20">
                  <p className="text-xs text-[#A78BFA] font-medium mb-1.5">
                    Set-up guide · <a href={guide.href} target="_blank" rel="noopener noreferrer" className="underline">{guide.href.replace('https://', '')}</a>
                  </p>
                  <ol className="text-[11px] text-slate-400 space-y-1 list-decimal list-inside">
                    {guide.steps.map((s) => <li key={s}>{s}</li>)}
                  </ol>
                </div>
              )}
            </GlassCard>

            <GlassCard className="p-6" data-testid="security-scripts-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={Code} ramp="cyan" />
                <div>
                  <h2 className="text-white font-bold font-syne">Custom head &amp; footer scripts</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    Paste your consent banner snippet, or any analytics/chat/pixel widget you can&rsquo;t configure through the other Settings pages.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-[#94A3B8] text-sm">Head scripts</Label>
                  <p className="text-[10px] text-slate-500 mb-1.5">Injected inside <span className="font-mono text-white/70">&lt;head&gt;</span> of every storefront page.</p>
                  <Textarea
                    rows={6}
                    value={state.head_scripts || ''}
                    onChange={(e) => setState((p) => ({ ...p, head_scripts: e.target.value }))}
                    placeholder={'<script src="https://app.termly.io/..."></script>'}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl font-mono text-xs"
                    data-testid="security-head-scripts"
                  />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-sm">Footer scripts</Label>
                  <p className="text-[10px] text-slate-500 mb-1.5">Injected before <span className="font-mono text-white/70">&lt;/body&gt;</span>. Great for chat widgets and analytics.</p>
                  <Textarea
                    rows={6}
                    value={state.footer_scripts || ''}
                    onChange={(e) => setState((p) => ({ ...p, footer_scripts: e.target.value }))}
                    placeholder={'<script>/* Analytics or chat widget */</script>'}
                    className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl font-mono text-xs"
                    data-testid="security-footer-scripts"
                  />
                </div>
              </div>
            </GlassCard>
          </>
        )}
      </div>
    </div>
  );
}
